// File: oneportal-shell/src/api/services/shopifyService.ts
// Shopify Storefront API service with GraphQL queries

import { shopifyStorefrontClient } from '../shopifyConfig';
import { PRODUCT_WITH_METAFIELDS_QUERY, PRODUCTS_WITH_METAFIELDS_QUERY } from '../shopifyQueries';

/**
 * Metafield type definition
 */
export interface Metafield {
  value: string;
  type: string;
}

/**
 * Product-level metafields (new namespace: product)
 */
export interface ProductMetafields {
  productNumber?: Metafield;
  proWorksWithResideoApp?: Metafield;
  proBrand?: Metafield;
  thermostatWiringCompatibility?: Metafield;
  websiteProductStatus?: Metafield;
  bestSellerBadge?: Metafield;
  compatibleThirdPartyApps?: Metafield;
  defaultRis?: Metafield;
  productAlternateContentTitle?: Metafield;
  productAlternateContentDescription?: Metafield;
  productDescriptionPro?: Metafield;
  // Legacy custom namespace
  subscriptionCode?: Metafield;
  subscriptionBillingServiceCode?: Metafield;
  resources?: Metafield;
}

/**
 * SKU/Variant-level metafields (new namespace: sku)
 */
export interface SkuMetafields {
  skuNumber?: Metafield;
  skuTitle?: Metafield;
  skuSpecs?: Metafield;
  skuAlternateContentTitle?: Metafield;
  channelDisplayCategory?: Metafield;
  planFamily?: Metafield;
  planSequence?: Metafield;
  recurringPrice?: Metafield;
  campaignPromotionalPrice?: Metafield;
  activatePromoFromDate?: Metafield;
  activatePromoToDate?: Metafield;
  brandingLogo?: Metafield; // reference
  defaultCategory?: Metafield; // reference
  productDelivery?: Metafield;
  smartAlertService?: Metafield;
  monthlySmartAlertPrice?: Metafield;
  monthlyTierOnePrice?: Metafield;
  monthlyTierTwoPrice?: Metafield;
  activationFeeText?: Metafield;
  unitPrice?: Metafield;
  websiteSkuStatus?: Metafield;
  soldByRetailer?: Metafield;
  skuOrder?: Metafield;
  proInstall?: Metafield;
  perksBadge?: Metafield;
  perksInclude?: Metafield;
  // Legacy custom namespace
  serviceTerm?: Metafield;
  subscriptionBillingId?: Metafield;
  bundleProducts?: Metafield;
  family?: Metafield;
  htmlContent?: Metafield;
  compatibilityApps?: Metafield;
}

/**
 * Collection-level metafields
 */
export interface CollectionMetafields {
  code?: Metafield;
  parentCode?: Metafield;
  metaTitle?: Metafield;
  ogTitle?: Metafield;
  filters?: Metafield;
  plpText?: Metafield;
}

/**
 * Product with comprehensive metafields
 */
export interface ProductWithMetafields extends ShopifyProduct {
  // Product metafields
  productNumber?: Metafield;
  proWorksWithResideoApp?: Metafield;
  proBrand?: Metafield;
  thermostatWiringCompatibility?: Metafield;
  websiteProductStatus?: Metafield;
  bestSellerBadge?: Metafield;
  compatibleThirdPartyApps?: Metafield;
  defaultRis?: Metafield;
  productAlternateContentTitle?: Metafield;
  productAlternateContentDescription?: Metafield;
  productDescriptionPro?: Metafield;
  subscriptionCode?: Metafield;
  subscriptionBillingServiceCode?: Metafield;
  resources?: Metafield;
  
  // Collections with metafields
  collections?: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        handle: string;
        code?: Metafield;
        parentCode?: Metafield;
        metaTitle?: Metafield;
        ogTitle?: Metafield;
        filters?: Metafield;
        plpText?: Metafield;
      };
    }>;
  };
  
  // Images array
  images?: {
    edges: Array<{
      node: {
        url: string;
        altText?: string;
      };
    }>;
  };
  
  // Variants with SKU metafields
  variants: {
    edges: Array<{
      node: ShopifyVariant & SkuMetafields;
    }>;
  };
}

/**
 * Shopify product type definitions
 */
export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  handle: string;
  featuredImage?: {
    url: string;
    altText?: string;
  };
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  variants: {
    edges: Array<{
      node: ShopifyVariant;
    }>;
  };
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  availableForSale: boolean;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

export interface ShopifyProductsResponse {
  products: {
    edges: Array<{
      node: ShopifyProduct;
    }>;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

export interface ShopifyProductResponse {
  product: ShopifyProduct;
}

/**
 * Shopify service for product catalog operations
 */
export const shopifyService = {
  /**
   * Get all products with optional metafields
   * @param first - Number of products to fetch (default: 20)
   * @param includeMetafields - Whether to include metafields (default: false)
   * @returns Array of products
   */
  async getProducts(first: number = 20, includeMetafields: boolean = false): Promise<ShopifyProduct[]> {
    // Use custom query if metafields requested
    const query = includeMetafields ? PRODUCTS_WITH_METAFIELDS_QUERY : `
      query GetProducts($first: Int!) {
        products(first: $first) {
          edges {
            node {
              id
              title
              description
              handle
              featuredImage {
                url
                altText
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    availableForSale
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `;

    try {
      const data = await shopifyStorefrontClient.query<ShopifyProductsResponse>(query, {
        first,
      });

      return data.products.edges.map((edge) => edge.node);
    } catch (error) {
      console.error('Failed to fetch Shopify products:', error);
      throw error;
    }
  },

  /**
   * Get product by handle with full metafields
   * Uses custom query with comprehensive metafields for product detail pages
   * @param handle - Product handle (URL-friendly identifier)
   * @returns Product with all metafields
   */
  async getProductByHandleWithMetafields(handle: string): Promise<any> {
    try {
      const data = await shopifyStorefrontClient.query<any>(
        PRODUCT_WITH_METAFIELDS_QUERY,
        { handle }
      );

      if (!data.product) {
        throw new Error(`Product not found: ${handle}`);
      }

      return data.product;
    } catch (error) {
      console.error(`Failed to fetch product with metafields (${handle}):`, error);
      throw error;
    }
  },

  /**
   * Get a single product by ID (GID) - default query without metafields
   * For basic product info when metafields not needed
   * @param productId - Shopify product ID (format: gid://shopify/Product/123)
   * @returns Product details
   */
  async getProductById(productId: string): Promise<ShopifyProduct> {
    const query = `
      query GetProduct($id: ID!) {
        product(id: $id) {
          id
          title
          description
          handle
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await shopifyStorefrontClient.query<ShopifyProductResponse>(query, {
        id: productId,
      });

      if (!data.product) {
        throw new Error(`Product not found: ${productId}`);
      }

      return data.product;
    } catch (error) {
      console.error(`Failed to fetch Shopify product ${productId}:`, error);
      throw error;
    }
  },

  /**
   * Get a single product by handle (URL-friendly identifier)
   * @param handle - Product handle (e.g., "pro-iq-engage")
   * @returns Product details
   */
  async getProductByHandle(handle: string): Promise<ShopifyProduct> {
    const query = `
      query GetProductByHandle($handle: String!) {
        product(handle: $handle) {
          id
          title
          description
          handle
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await shopifyStorefrontClient.query<ShopifyProductResponse>(query, {
        handle,
      });

      if (!data.product) {
        throw new Error(`Product not found with handle: ${handle}`);
      }

      return data.product;
    } catch (error) {
      console.error(`Failed to fetch Shopify product by handle ${handle}:`, error);
      throw error;
    }
  },

  /**
   * Get products by collection handle
   * @param collectionHandle - Collection handle (e.g., "subscriptions", "services")
   * @param first - Number of products to fetch (default: 20)
   * @returns Array of products in the collection
   */
  async getProductsByCollection(
    collectionHandle: string,
    first: number = 20
  ): Promise<ShopifyProduct[]> {
    const query = `
      query GetCollection($handle: String!, $first: Int!) {
        collection(handle: $handle) {
          products(first: $first) {
            edges {
              node {
                id
                title
                description
                handle
                featuredImage {
                  url
                  altText
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await shopifyStorefrontClient.query<{
        collection: { products: ShopifyProductsResponse['products'] };
      }>(query, {
        handle: collectionHandle,
        first,
      });

      if (!data.collection?.products) {
        return [];
      }

      return data.collection.products.edges.map((edge) => edge.node);
    } catch (error) {
      console.error(
        `Failed to fetch products from collection ${collectionHandle}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Generate Shopify checkout URL for a product variant
   * @param variantId - Shopify variant ID (format: gid://shopify/ProductVariant/123)
   * @param quantity - Quantity to add to cart (default: 1)
   * @returns Checkout URL
   */
  generateCheckoutUrl(variantId: string, quantity: number = 1): string {
    // Extract numeric ID from Shopify GID format
    const numericId = variantId.split('/').pop();
    
    // Generate Shopify checkout URL with variant
    const shopifyDomain = import.meta.env.VITE_SHOPIFY_STOREFRONT_URL?.split('/api/')[0];
    
    if (!shopifyDomain) {
      throw new Error('Shopify domain not configured');
    }

    return `${shopifyDomain}/cart/${numericId}:${quantity}`;
  },
};
